import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Info, CheckCircle, AlertTriangle } from 'lucide-react';
import { notificationService } from '../services/notificationService';

export default function NotificationBar() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dismissedIds, setDismissedIds] = useState(() => {
    const saved = localStorage.getItem('dismissed_notifications');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    loadNotifications();
    
    // Subscribe to realtime changes
    const subscription = notificationService?.subscribeToNotifications((payload) => {
      console.log('Notification realtime update:', payload);
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
        loadNotifications();
      } else if (payload?.eventType === 'DELETE') {
        setNotifications(prev => prev?.filter(n => n?.id !== payload?.old?.id));
      }
    });

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, []);

  const loadNotifications = async () => {
    try {
      setError(null);
      console.log('Fetching notifications...');
      const data = await notificationService?.getActiveNotifications();
      console.log('Notifications fetched:', data);
      setNotifications(data || []);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(err?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = (id) => {
    const newDismissedIds = [...dismissedIds, id];
    setDismissedIds(newDismissedIds);
    localStorage.setItem('dismissed_notifications', JSON.stringify(newDismissedIds));
  };

  const getIcon = (type) => {
    switch (type) {
      case 'error':
        return <AlertCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getColors = (type) => {
    switch (type) {
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  // Filter out dismissed notifications
  const activeNotifications = notifications?.filter(n => !dismissedIds?.includes(n?.id));

  // Show error state if there's an error
  if (error) {
    console.error('NotificationBar error state:', error);
  }

  // Don't render if loading or no active notifications
  if (loading || activeNotifications?.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-20 left-0 right-0 z-[9999] px-4 pointer-events-none">
      <div className="max-w-7xl mx-auto space-y-3 pointer-events-auto">
        {activeNotifications?.map((notification) => (
          <div
            key={notification?.id}
            className={`${getColors(notification?.type)} border-2 rounded-lg shadow-lg p-4 flex items-start justify-between animate-in slide-in-from-top duration-300`}
          >
            <div className="flex items-start gap-3 flex-1">
              <div className="flex-shrink-0 mt-0.5">
                {getIcon(notification?.type)}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm mb-1">
                  {notification?.title}
                </h4>
                <p className="text-sm">
                  {notification?.message}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleDismiss(notification?.id)}
              className="flex-shrink-0 ml-4 p-1 hover:bg-black/10 rounded transition-colors"
              aria-label="Dismiss notification"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}